import {
	Button,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@dune/ui";
import { constructionSiteDocumentTypeLabels } from "@dune/vars";
import { Check, Loader2 } from "lucide-react";
import { useState } from "react";
import { useGetReferenceableFiles } from "../../../api/queries/constructionSiteSubmission.queries";

interface ReferenceDocumentModalProps {
	isOpen: boolean;
	onClose: () => void;
	constructionSiteId: number;
	onSelectFile?: (fileId: string) => void | Promise<void>;
}

export function ReferenceDocumentModal({
	isOpen,
	onClose,
	constructionSiteId,
	onSelectFile,
}: ReferenceDocumentModalProps) {
	const { data: referenceableFiles, isLoading } =
		useGetReferenceableFiles(constructionSiteId);
	const [addedFileIds, setAddedFileIds] = useState<Set<string>>(new Set());
	const [loadingFileIds, setLoadingFileIds] = useState<Set<string>>(new Set());

	const handleSelectFile = async (fileId: string) => {
		setLoadingFileIds((previous) => new Set(previous).add(fileId));
		try {
			await onSelectFile?.(fileId);
			setAddedFileIds((previous) => new Set(previous).add(fileId));
		} finally {
			setLoadingFileIds((previous) => {
				const next = new Set(previous);
				next.delete(fileId);
				return next;
			});
		}
	};

	const handleOpenChange = (open: boolean) => {
		if (!open) {
			setAddedFileIds(new Set());
			setLoadingFileIds(new Set());
			onClose();
		}
	};

	return (
		<Dialog open={isOpen} onOpenChange={handleOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Référencer un document existant</DialogTitle>
				</DialogHeader>

				<div className="flex-1 overflow-y-auto">
					{isLoading ? (
						<div className="flex items-center justify-center py-8">
							<Loader2 className="w-6 h-6 animate-spin" />
							<span className="ml-2">Chargement des documents...</span>
						</div>
					) : !referenceableFiles || referenceableFiles.length === 0 ? (
						<div className="text-center py-8 text-muted-foreground">
							Aucun document disponible pour le référencement
						</div>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Nom</TableHead>
									<TableHead>Type</TableHead>
									<TableHead />
								</TableRow>
							</TableHeader>
							<TableBody>
								{referenceableFiles.map((file) => (
									<TableRow key={file.id}>
										<TableCell
											colSpan={1}
											className="font-medium truncate"
										>
											{file.name}
										</TableCell>
										<TableCell>
											{constructionSiteDocumentTypeLabels[file.type]}
										</TableCell>
										<TableCell>
											{addedFileIds.has(file.id) ? (
												<span className="text-sm text-green-600 font-medium flex items-center gap-1">
													<Check className="w-4 h-4" />
													Ajouté
												</span>
											) : (
												<Button
													size="sm"
													disabled={loadingFileIds.has(file.id)}
													isLoading={loadingFileIds.has(file.id)}
													onClick={() => handleSelectFile(file.id)}
												>
													Choisir
												</Button>
											)}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={() => handleOpenChange(false)}>
						Fermer
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
